import { IOption } from 'components/_form/Select/Select';
import { IArea } from './area';
import { IPagination } from './pagination';
import { IBlock, IPractice } from './methologies';
import { IPredefinedTask } from './task';
import { IUserWithoutProfile } from './user';
import { IAssessmentsClosing } from './assessmentsClosings';
import { INote } from './notes';

export interface IScore {
  id: number;
  score: number;
  comment: string;
  guideline: string;
  criterion_id: number;
  assessment_id: number;
  task_ids: number[];
  predefinedTasks: [];
}

export interface TableScore {
  id?: number;
  name: string;
  value?: string;
  assessmentId: number;
  criterionId?: number;
  areaId: number;
}

export interface Criterion {
  id: number;
  content: string;
  assessment_instruction: string;
  score?: IScore | null;
  predefined_tasks: IPredefinedTask[];
}

export interface BlockInAssessment {
  id: number;
  name: string;
  avg_score: number;
  criteria: Criterion[];
  column: number;
  implementation_steps: [];
  theme_id: number;
  stage_id: number;
  documents: [];
}

export interface IAssessment {
  id: number;
  date: string;
  due_date: string;
  number: string;
  group: string;
  updated_at: string;
  author: IUserWithoutProfile;
  block: Omit<IBlock, 'document_urls'>;
  avg_score?: string;
  assessment_type: 'normal' | 'target' | 'baseline' | 'current' | 'derivative';
  name: string;
  description: {
    id: number;
    name: string;
    body: string;
    record_type: string;
    record_id: number;
    created_at: string;
    updated_at: string;
  };
  version: string;
  realization_date: string;
  methodology: {
    id: number;
    name: string;
  };
  practice: {
    id: number;
    name: string;
    state: string;
  };
  scores: IScore[];
  area: IArea;
  related_to_assessment: IAssessment;
  leader: IUserWithoutProfile;
  team_leader: IUserWithoutProfile;
  team_manager: IUserWithoutProfile;
  teams: {
    id: string;
    id_code: string;
    name: string;
    description: string;
    state: string;
    image_urls: string[];
    logo_url: string;
  }[];
  planned_duration_iso8601: string;
  duration_iso8601: string;
  notes?: INote[];
  planned_start_date: string;
  planned_completion_date: string;
  only_realization?: boolean;
  start_time: string;
  completion_time: string;
  assessments_closings: IAssessmentsClosing[];
}

export interface IAssessmentPractice extends Omit<IPractice, 'blocks'> {
  blocks: BlockInAssessment[];
}

export interface IDetailedAssessment
  extends Omit<IAssessment, 'practice' | 'assessment_participations'> {
  practice: IAssessmentPractice;
  assessment_participations?: {
    id: string;
    participant: {
      id: string;
      email: string;
      role: string;
      username: string;
      company_id: string;
      photo: string;
      first_name: string;
      last_name: string;
    };
    function: {
      id: string;
      name: string;
      state: string;
      created_at: string;
      updated_at: string;
    };
  }[];
}

export interface IAssessmentResponse {
  data: IDetailedAssessment;
}

export interface IAssessmentsRequest {
  filters?: {
    area_ids?: number[];
  };
}

export interface IAssessmentsResponse extends IPagination {
  data: IAssessment[];
}

export type AssessmentFilters = {
  area_ids: {
    value: number[];
  };
};

export const initAssessmentFilters = {
  area_ids: {
    value: []
  }
};

export interface BlockScore {
  goal: string | null;
  current: string | null;
  external: string | null;
}

export interface SelectedAssessment {
  assessmentNumber: string;
}

export const initSelectedAssessment: SelectedAssessment = {
  assessmentNumber: '0'
};

export type CreateAssessmentBody = {
  realization_date: string | IOption;
  assessment_type: string | IOption;
  version: string | IOption;
  date: string | IOption;
  name: string | IOption;
  practice_id: number | IOption;
  area_id: string;
  description: string;
  group: string;
  related_to_assessment_id: string;
  notes: INote[];
  author_id?: string;
  leader_id?: string;
  team_leader_id?: string;
  team_manager_id?: string;
  archived_at?: string;
  planned_start_date?: string;
  planned_completion_date?: string;
  planned_duration: string;
  only_realization?: boolean;
  start_time?: string;
  completion_time?: string;
  duration: string;
  assessment_participations_attributes?: {
    id?: string;
    participant_id?: string;
    function_id?: string;
    _destroy?: number;
  }[];
  team_ids?: string[];
  closing_ids?: string[];
};

export interface CreateAssessmentResponse {}

export enum ModalTypeList {
  CreateAssessment = 'createAssessment',
  EditAssessment = 'editAssessment'
}

// GRAPH TYPES

export interface RadarGraphDataset {
  label: string;
  data: number[];
  backgroundColor: string;
  borderColor: string;
  borderWidth: number;
}
